import { DimeToggleGroup, DimeToggleGroupItem } from '@nudool/design-system';
import { formatCurrency } from '@nudool/shared';

import type { InventorySection } from '../inventory-item-list/use-inventory-sections.js';
import { INVENTORY_COST_CHECK_LABELS } from './cost-check-labels.js';
import { costCheckTagCounts } from './cost-check-tag-counts.js';
import type { InventoryCostCheck } from './inventory-cost-check.js';
import { inventoryRollupCost } from './rollup-cost.js';

const COST_CHECKS = Object.keys(INVENTORY_COST_CHECK_LABELS) as InventoryCostCheck[];
const PROBLEM_CHECK: InventoryCostCheck = 'FAR';
// Stock values are never negative, so an uncosted brand sorts below every costed one.
const UNCOSTED_LAST = -1;

function valueText(costOnHand: number | null, stocked: number): string {
  if (costOnHand === null) return stocked > 0 ? 'Not costed' : (formatCurrency(0) ?? '');
  return formatCurrency(costOnHand) ?? '';
}

function coverageText(costed: number, stocked: number): string {
  if (stocked === 0) return 'Nothing in stock';
  if (costed === stocked) return `All ${stocked} tags have a cost on file`;
  return `${costed} of ${stocked} tags have a cost on file`;
}

/** The cost view's summary in place of Stock health: what the stock is worth, and the chips that find the costs worth a second look. */
export function InventoryValueSummary({
  sections,
  loading,
  costCheck,
  onCostCheckChange,
}: Readonly<{
  sections: InventorySection[];
  loading: boolean;
  costCheck: InventoryCostCheck | undefined;
  onCostCheckChange: (check: InventoryCostCheck | undefined) => void;
}>) {
  const items = sections.flatMap((section) => section.items);
  const total = inventoryRollupCost(items);
  const counts = costCheckTagCounts(items);
  const brands = sections
    .flatMap((section) =>
      section.brand ? [{ ...section.brand, cost: inventoryRollupCost(section.items) }] : [],
    )
    .sort(
      (left, right) =>
        (right.cost.costOnHand ?? UNCOSTED_LAST) - (left.cost.costOnHand ?? UNCOSTED_LAST),
    );

  return (
    <>
      <div className="flex flex-col gap-0.5">
        <h2 className="text-[13px] font-semibold leading-[18px]">Value on hand</h2>
        <span
          data-testid="inventory-value-on-hand"
          className="text-[22px] font-semibold leading-[30px] tabular-nums"
        >
          {loading ? '—' : valueText(total.costOnHand, total.stockedTagCount)}
        </span>
        <span className="text-xs text-muted-foreground">
          {loading ? ' ' : coverageText(total.costedTagCount, total.stockedTagCount)}
        </span>
      </div>
      <div className="flex min-w-0 flex-1 basis-[420px] flex-col gap-2.5">
        <h2 className="text-[13px] font-semibold leading-[18px]">Worth a look</h2>
        <DimeToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={costCheck ?? ''}
          aria-label="Cost checks"
          onValueChange={(nextValue) =>
            onCostCheckChange(nextValue === '' ? undefined : (nextValue as InventoryCostCheck))
          }
          className="flex flex-wrap justify-start gap-1.5"
        >
          {COST_CHECKS.map((check) => {
            const count = counts[check];
            const text = `${count} ${count === 1 ? 'tag' : 'tags'} ${INVENTORY_COST_CHECK_LABELS[check]}`;
            const alert = check === PROBLEM_CHECK && count > 0;
            return (
              <DimeToggleGroupItem
                key={check}
                value={check}
                disabled={loading}
                aria-label={`${text}. Filter the table.`}
                className={`h-8 cursor-pointer rounded-full px-3 text-[13px] font-normal data-[state=on]:border-foreground ${
                  alert
                    ? 'border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                    : ''
                }`.trim()}
              >
                <span aria-hidden className={count === 0 ? 'text-muted-foreground' : undefined}>
                  {text}
                </span>
              </DimeToggleGroupItem>
            );
          })}
        </DimeToggleGroup>
        <span className="text-xs text-muted-foreground">
          Far means under half or over double the part&apos;s standard cost.
        </span>
      </div>
      {brands.length > 1 && (
        <div className="ml-auto flex flex-col gap-2">
          <h2 id="inventory-value-by-brand" className="text-[13px] font-semibold leading-[18px]">
            By brand
          </h2>
          <ul
            aria-labelledby="inventory-value-by-brand"
            className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 text-[13px]"
          >
            {brands.map((brand) => (
              <li key={brand.id} className="contents">
                <span className="text-muted-foreground">{brand.name}</span>
                <span className="text-right font-medium tabular-nums">
                  {valueText(brand.cost.costOnHand, brand.cost.stockedTagCount)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
